import {
  Box,
  CircularProgress,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import { JSX, ReactNode, useRef } from "react";
import { useLocalization } from "../hooks/useLocalization";
import { RemovableChip } from "./RemovableChip";

export interface SelectableListItem {
  id: string;
  primaryText: string;
  secondaryText?: string;
  tertiaryText?: string;
  type?: "disabled" | "hasAccess" | "selectable";
}

export interface SelectableListProps<T extends SelectableListItem> {
  items?: T[];
  isLoading?: boolean;
  selectedId?: string | null;
  selectedIds?: string[];
  onItemSelect: (id: string) => void;
  renderItem?: (item: T) => ReactNode;
  emptyMessage?: string;
  maxHeight?: number | string;
  multiSelect?: boolean;
  showSelectedChips?: boolean;
  onChipRemove?: (id: string) => void;
  selectedItemsTitle?: ReactNode | string;
  selectedItems?: SelectableListItem[];
  disabled?: boolean;
  height?: number | string;
  minHeight?: number | string;
  sx?: SxProps<Theme>;
}

export const SelectableList = <T extends SelectableListItem>({
  items,
  isLoading = false,
  selectedId,
  selectedIds = [],
  onItemSelect,
  renderItem,
  emptyMessage,
  maxHeight = 300,
  height,
  minHeight = 300,
  multiSelect = false,
  showSelectedChips = true,
  onChipRemove,
  selectedItemsTitle,
  selectedItems = [],
  disabled = false,
  sx,
}: SelectableListProps<T>): JSX.Element => {
  const { t } = useLocalization();
  const theme = useTheme();
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: items?.length ?? 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 68,
    overscan: 5,
  });

  const getItemStyles = (item: T, isSelected: boolean) => {
    const isItemDisabled = item.type === "disabled";
    const hasAccess = item.type === "hasAccess";

    if (isItemDisabled) {
      return {
        opacity: 0.5,
      };
    }

    if (hasAccess) {
      return {
        backgroundColor: theme.colors.secondary[0],
        opacity: 0.6,
      };
    }

    if (isSelected) {
      return {
        backgroundColor: theme.colors.grey,
        cursor: "pointer",
      };
    }

    return {
      cursor: "pointer",
      "&:hover": { backgroundColor: theme.colors.secondary[0] },
    };
  };

  const defaultRenderItem = (item: T) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 14,
            color: theme.colors.text[7],
            mb: 0.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.primaryText}
        </Typography>
        {item.secondaryText && (
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: theme.colors.text[7],
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.secondaryText}
          </Typography>
        )}
      </Box>
      {item.tertiaryText && (
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 400,
            color: theme.colors.text[2],
            width: 100,
            textAlign: "left",
          }}
        >
          {item.tertiaryText}
        </Typography>
      )}
    </Box>
  );

  return (
    <Stack mt={1} spacing={3} sx={sx}>
      <Box
        ref={parentRef}
        sx={{
          maxHeight,
          height,
          minHeight,
          borderRadius: "16px",
          border: `1px solid ${theme.colors.secondary[1]}`,
          overflowY: "auto",
          padding: "8px",
          scrollbarGutter: "stable both-edges",
          userSelect: "none",
        }}
      >
        {isLoading && (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              height: "100%",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {!isLoading && (!items || items.length === 0) && (
          <Typography
            sx={{
              fontSize: 14,
              color: theme.colors.text[3],
              textAlign: "center",
              py: 2,
            }}
          >
            {emptyMessage ?? t("components.selectableList.noItemsFound")}
          </Typography>
        )}
        {!isLoading && items && items.length > 0 && (
          <Box
            sx={{
              height: `${virtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const item = items[virtualRow.index];
              if (!item) return null;

              const isSelected = multiSelect
                ? selectedIds.includes(item.id)
                : selectedId === item.id;

              const isItemDisabled =
                item.type === "disabled" || item.type === "hasAccess";

              return (
                <Box
                  key={item.id}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                    paddingY: "4px",
                    borderBottom:
                      virtualRow.index < items.length - 1
                        ? `1px solid ${theme.colors.secondary[1]}`
                        : "none",
                  }}
                >
                  <Box
                    aria-disabled={isItemDisabled}
                    data-disabled={isItemDisabled}
                    onClick={() =>
                      !disabled && !isItemDisabled && onItemSelect(item.id)
                    }
                    sx={{
                      p: "8px",
                      borderRadius: "8px",
                      ...getItemStyles(item, isSelected),
                    }}
                  >
                    {renderItem ? renderItem(item) : defaultRenderItem(item)}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
      {showSelectedChips && (
        <Box sx={{ mb: 2 }}>
          {typeof selectedItemsTitle === "string" ? (
            <Typography fontSize={12}>{selectedItemsTitle}</Typography>
          ) : (
            (selectedItemsTitle ?? (
              <Typography fontSize={12}>
                {t("components.selectableList.selectedItems")}
              </Typography>
            ))
          )}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              maxHeight: 120,
              minHeight: 40,
              overflowY: "auto",
              paddingTop: 1,
            }}
          >
            {selectedItems.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: theme.colors.text[1],
                  }}
                >
                  {t("components.selectableList.noEntrySelected")}
                </Typography>
              </Box>
            ) : (
              selectedItems.map((item) => (
                <RemovableChip
                  disabled={disabled}
                  key={item.id}
                  label={item.primaryText}
                  onDelete={
                    onChipRemove && !disabled
                      ? () => onChipRemove(item.id)
                      : undefined
                  }
                />
              ))
            )}
          </Box>
        </Box>
      )}
    </Stack>
  );
};
